import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import BottomNav from '../components/BottomNav';
import OnboardingProgressHeader from '../components/OnboardingProgressHeader';
import PrimaryButton from '../components/PrimaryButton';
import starFilledIcon from '../assets/icon-star-filled.svg';
import starOutlineIcon from '../assets/icon-star-outline.svg';
import { getAliyahMetas, getAliyahLabel } from '../utils/aliyot';
import { getHaftaraTitle } from '../utils/haftarot';
import {
  getParasha,
  getTradition,
  getLearningTargets,
  setLearningTargets,
  LearningTargetKey,
} from '../utils/userSelections';
import { trackClick, trackNavigation, trackPageView, trackSelection } from '../utils/analytics';

interface ReadingOption {
  key: LearningTargetKey;
  label: string;
  subtitle?: string;
}

const FALLBACK_ALIYOT_COUNT = 7; // 6 aliyot + maftir when no parasha is selected yet

const ChooseReadingsPage: React.FC = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const isHebrew = i18n.language?.startsWith('he') ?? true;

  const parasha = getParasha();
  const traditionKey = getTradition()?.key ?? null;

  const haftaraTitle = useMemo<string | null>(
    () => (parasha ? getHaftaraTitle(parasha.english, traditionKey) : null),
    [parasha, traditionKey],
  );

  const options = useMemo<ReadingOption[]>(() => {
    const aliyahMetas = parasha ? getAliyahMetas(parasha.english, isHebrew) : [];
    const aliyahOptions: ReadingOption[] =
      aliyahMetas.length > 0
        ? aliyahMetas.map((meta, idx) => ({
            key: `aliyah_${idx}` as LearningTargetKey,
            label: meta.label,
            subtitle:
              meta.verseStart > 0
                ? (t('parasha_overview_verses', {
                    start: meta.verseStart,
                    end: meta.verseEnd,
                  }) as string)
                : undefined,
          }))
        : Array.from({ length: FALLBACK_ALIYOT_COUNT }, (_, idx) => ({
            key: `aliyah_${idx}` as LearningTargetKey,
            label: getAliyahLabel(idx, isHebrew),
          }));

    return [
      {
        key: 'haftara' as LearningTargetKey,
        label: haftaraTitle
          ? (t('haftara_with_title', { title: haftaraTitle }) as string)
          : (t('parasha_overview_haftara', 'הפטרה') as string),
      },
      ...aliyahOptions,
      {
        key: 'birchot_hatorah' as LearningTargetKey,
        label: t('parasha_overview_birchot_hatorah', 'ברכות התורה') as string,
        subtitle: t(
          'parasha_overview_birchot_hatorah_subtitle',
          'נקראות לפני ואחרי קריאה בתורה',
        ) as string,
      },
      {
        key: 'shema' as LearningTargetKey,
        label: t('parasha_overview_shema', 'שמע ישראל') as string,
        subtitle: t(
          'choose_readings_shema_subtitle',
          'נהוג לקרוא כל יום עם הנחת התפילין',
        ) as string,
      },
    ];
  }, [parasha, isHebrew, haftaraTitle, t]);

  const maftirKey = useMemo<LearningTargetKey>(() => {
    const aliyahKeys = options.filter((o) => o.key.startsWith('aliyah_'));
    return aliyahKeys.length > 0 ? aliyahKeys[aliyahKeys.length - 1].key : 'aliyah_6';
  }, [options]);

  const [selected, setSelected] = useState<LearningTargetKey[]>(() => {
    const saved = getLearningTargets();
    if (saved !== null) return saved;
    return [];
  });

  // Default selection (typical bar-mitzvah set) once options are known and nothing was saved before
  useEffect(() => {
    if (getLearningTargets() === null && selected.length === 0) {
      setSelected(['haftara', maftirKey, 'birchot_hatorah', 'shema']);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [maftirKey]);

  useEffect(() => {
    trackPageView('ChooseReadings');
  }, []);

  const toggle = (key: LearningTargetKey): void => {
    trackSelection('learning_target', key, { selected: !selected.includes(key) });
    setSelected((prev) => (prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]));
  };

  const handleContinue = (): void => {
    trackClick('Continue', 'ChooseReadings', { targets: selected.join(',') });
    setLearningTargets(selected);
    trackNavigation('ChooseReadings', 'UserPortal', 'button');
    navigate('/user-portal');
  };

  const handleSkip = (): void => {
    trackClick('Skip', 'ChooseReadings');
    trackNavigation('ChooseReadings', 'UserPortal', 'skip');
    navigate('/user-portal');
  };

  return (
    <div
      className="mx-auto w-full max-w-[980px] flex flex-col"
      style={{ flex: 1, minHeight: 0, overflow: 'hidden' }}
    >
      <OnboardingProgressHeader step={4} />
      <main className="flex-1 overflow-y-auto px-4 pt-4 pb-4">
        <h2 className="text-center text-xl font-extrabold mb-4 text-[#13294B]">
          {t('choose_readings_title', 'בחר מה בכוונתך ללמוד')}
        </h2>

        <div dir="rtl" className="flex flex-col gap-2.5">
          {options.map(({ key, label, subtitle }) => {
            const isSelected = selected.includes(key);
            return (
              <button
                key={key}
                type="button"
                onClick={() => toggle(key)}
                aria-pressed={isSelected}
                className={
                  'w-full flex items-center gap-3 rounded-[17px] border border-[#6B7280] px-4 py-3 text-start transition-colors duration-200 focus:outline-none ' +
                  (isSelected ? 'bg-[#DDE9F9]' : 'bg-white')
                }
              >
                <img
                  src={isSelected ? starFilledIcon : starOutlineIcon}
                  alt=""
                  aria-hidden="true"
                  className="w-6 h-6 flex-shrink-0"
                />
                <span className="flex-shrink-0 whitespace-nowrap text-base font-bold text-black">
                  {label}
                </span>
                {subtitle && (
                  <span className="flex-1 min-w-0 text-sm text-[#6B7280] truncate">{subtitle}</span>
                )}
              </button>
            );
          })}
        </div>

        <p className="text-center text-sm text-[#6B7280] mt-4 mb-4">
          {t(
            'choose_readings_hint',
            'כדאי להתייעץ עם ההורים או בית הכנסת המקומי, ניתן לשנות את הבחירה בכל עת',
          )}
        </p>
      </main>
      <div className="flex-shrink-0 bg-white border-t border-[#E4E7EB] px-4 pt-3">
        <PrimaryButton
          onClick={handleContinue}
          text={t('continue', 'המשך')}
          variant="gold"
          style={{ fontWeight: 700, fontSize: 18 }}
        />
        <button
          type="button"
          onClick={handleSkip}
          className="block mx-auto m-0 p-0 text-base text-[#4B5563] bg-transparent border-none cursor-pointer"
        >
          {t('choose_readings_skip', 'דלג')}
        </button>
      </div>
      <BottomNav />
    </div>
  );
};

export default ChooseReadingsPage;
